/**
 * useActionSync - Syncs project actions (create/delete objects) with remote updates via Pusher
 *
 * This hook:
 * 1. Listens for Pusher events when objects are created/deleted by other users
 * 2. Refetches the project data to update the Apollo cache
 */

import { useEffect, useRef } from 'react';
import { subscribeToPrivateProjectChannel, clientId } from '../pusher';
import { useProjectByIdLazyQuery, useProjectAnnotationStateLazyQuery } from '../../types/graphqlTypes';
import type { Channel } from 'pusher-js';

/** Event payload for action execution */
interface ActionExecutedEvent {
  actionType: string;
  actionIdentifier: string;
  executedBy?: string;
  clientId?: string;
}

export interface UseActionSyncOptions {
  projectId: string;
  /** Called when a remote action is received */
  onRemoteAction?: (actionType: string, actionIdentifier: string) => void;
}

/**
 * Hook that syncs project actions (groups, layers, ordering) with remote Pusher updates.
 * Annotation create/delete is handled by useAnnotationSync instead.
 */
export const useActionSync = (options: UseActionSyncOptions): void => {
  const { projectId, onRemoteAction } = options;
  const channelRef = useRef<Channel | null>(null);
  const onRemoteActionRef = useRef(onRemoteAction);
  const [fetchProject] = useProjectByIdLazyQuery({ fetchPolicy: 'network-only' });
  const [fetchAnnotationState] = useProjectAnnotationStateLazyQuery({ fetchPolicy: 'network-only' });

  useEffect(() => {
    onRemoteActionRef.current = onRemoteAction;
  });

  useEffect(() => {
    if (!projectId) return;

    // Subscribe to private channel for action events
    const channel = subscribeToPrivateProjectChannel(projectId);
    channelRef.current = channel;

    /**
     * Handle action execution from remote.
     * Refetches both the project shell and annotation state to pick up structural changes
     * (groups, layers, ordering). Both queries must be refetched because the annotation
     * context reads from PROJECT_ANNOTATION_STATE (cache-only) which may not be updated
     * by the PROJECT query refetch alone.
     */
    const handleActionExecuted = async (event: ActionExecutedEvent) => {
      // Ignore actions triggered by this client — optimistic cache writes
      // already handled the local state. Without this filter, the refetch
      // overwrites optimistic writes with stale server state.
      if (event.clientId === clientId) return;

      try {
        await Promise.all([
          fetchProject({ variables: { projectId } }),
          fetchAnnotationState({ variables: { projectId } }),
        ]);
      } catch (e) {
        // A newer event re-executed the lazy query, which aborts this in-flight one.
        // The newer fetch picks up the latest state, so this one can be dropped.
        if (e instanceof Error && e.name === 'AbortError') return;
        throw e;
      }

      onRemoteActionRef.current?.(event.actionType, event.actionIdentifier);
    };

    // Bind event handler
    channel.bind('action-executed', handleActionExecuted);

    return () => {
      // Only unbind our handler — don't unsubscribe the channel
      // (other hooks share the same private channel subscription)
      channel.unbind('action-executed', handleActionExecuted);
      channelRef.current = null;
    };
  }, [projectId, fetchProject, fetchAnnotationState]);
};
